<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import VehicleSelectWithAvatar from "@/components/VehicleSelectWithAvatar.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";

const props = defineProps({
  modalId: { type: String, required: true },
  vehicleOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close", "confirm"]);

const modalAddRouteVehicle = ref(null);
const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const snackbar = ref(null);
const form = ref({
  vehicleId: "",
});
const rules = {
  vehicleId: {
    required: helpers.withMessage("Vehículo es requerido", required),
  },
};
const v$ = useVuelidate(rules, form);

const hasVehicleOptions = computed(() => props.vehicleOptions.length > 0);

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo completar la acción.",
    type: "danger",
    color: "white",
  };
}

function handleHide() {
  form.value.vehicleId = "";
  v$.value.$reset();
  closeSnackbar();
  emit("close");
}

async function confirmAddVehicle() {
  if (props.isSubmitting) return;
  await v$.value.$validate();
  if (v$.value.$errors.length > 0) {
    showError(v$.value.$errors[0].$message);
    return;
  }

  closeSnackbar();
  emit("confirm", { vehicleId: form.value.vehicleId });
  modalAddRouteVehicle.value?.closeModal();
}

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
});
</script>

<template>
  <modal-base
    :id="modalId"
    ref="modalAddRouteVehicle"
    width="30rem"
    @hide="handleHide"
  >
    <template #header>
      <h6 class="mb-0">Añadir Vehículo</h6>
    </template>

    <template #body>
      <div class="row align-items-center">
        <div class="col">
          <vehicle-select-with-avatar
            id="add-route-vehicle"
            :model-value="form.vehicleId"
            :options="vehicleOptions"
            :error="v$.vehicleId.$error"
            :filterable="false"
            placeholder="Selecciona un vehículo"
            menu-width="255"
            @update:model-value="(value) => (form.vehicleId = value)"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="isSubmitting ? 'Añadiendo...' : 'Aceptar'"
        :loading="isSubmitting"
        :disable-button="isSubmitting || !hasVehicleOptions"
        @click="confirmAddVehicle"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
